import {
  AdminApiError,
  type AdminClient,
  type TenantStaffCatalogue,
  type TenantStaffMember,
} from '@apx/admin-sdk';
import { escapeHtml } from './reports-shared.js';
import { askConfirm } from '../modal.js';
import { showToast } from '../toast.js';

export interface TenantStaffPageDeps {
  client: AdminClient;
  integratorId: string;
  /** The signed-in user, so the page can refuse to let them strip themselves. */
  currentUserId: string;
}


/**
 * Human labels for the capability checklist. An integrator's office manager
 * should not have to read `game.bet_limits.write` and infer what it lets
 * someone do.
 */
const LABELS: Record<string, { label: string; hint: string }> = {
  'integrator.read': { label: 'View account', hint: 'See this integrator’s own settings and status' },
  'report.financial.read': {
    label: 'View revenue reports',
    hint: 'P/L, GGR, house take, per-player turnover — the money',
  },
  'report.activity.read': {
    label: 'View activity reports',
    hint: 'Play counts, bet feed and retention, without revenue figures',
  },
  'player.read': { label: 'View players', hint: 'Player list, detail and game history' },
  'player.ban': { label: 'Ban players', hint: 'Ban, unban and anonymise players' },
  'audit.read': { label: 'View audit log', hint: 'Who changed what, and when' },
  'game.config.read': { label: 'View game config', hint: 'Read-only view of game settings' },
  'game.catalog.write': { label: 'Manage catalogue', hint: 'Which games are offered to players, and their lobby order' },
  'game.content.write': { label: 'Edit game content', hint: 'Rules copy, leaderboard, streak and big-winner display' },
  'game.bet_limits.write': { label: 'Set bet limits', hint: 'Table limits, within the range ΛPX allows' },
  'integrator.settings.write': { label: 'Edit settings', hint: 'Display name, locale and branding' },
  'webhook.write': { label: 'Manage webhooks', hint: 'Add, edit and test webhook endpoints' },
  'api_key.write': { label: 'Manage API keys', hint: 'Create and revoke this integrator’s API keys' },
  'tenant_user.manage': { label: 'Manage staff', hint: 'Add colleagues and set what they can do' },
};

function labelFor(perm: string): { label: string; hint: string } {
  return LABELS[perm] ?? { label: perm, hint: '' };
}

export async function renderTenantStaffPage(
  root: HTMLElement,
  deps: TenantStaffPageDeps,
): Promise<void> {
  root.innerHTML = `
    <div class="sticky-head">
      <h1 class="page-title">Staff</h1>
      <p class="page-sub">
        Staff members who can sign in to this integrator’s console. Each is granted exactly the
        capabilities you select — never more than you hold yourself.
      </p>
    </div>
    <div id="staff-body"><p class="muted">Loading…</p></div>`;

  const bodyEl = root.querySelector<HTMLElement>('#staff-body')!;

  let catalogue: TenantStaffCatalogue = { permissions: [], presets: [] };
  let staff: TenantStaffMember[] = [];

  async function load(): Promise<void> {
    const [cat, list] = await Promise.all([
      deps.client.tenantStaffCatalogue(deps.integratorId),
      deps.client.listTenantStaff(deps.integratorId),
    ]);
    catalogue = cat;
    staff = list.staff;
  }

  function checklist(idPrefix: string, selected: ReadonlySet<string>): string {
    return catalogue.permissions
      .map((perm) => {
        const { label, hint } = labelFor(perm);
        return `
          <label class="staff-cap">
            <input type="checkbox" data-cap="${escapeHtml(perm)}" id="${idPrefix}-${escapeHtml(perm)}"
                   ${selected.has(perm) ? 'checked' : ''}>
            <span>
              <strong>${escapeHtml(label)}</strong>
              ${hint ? `<span class="muted staff-cap-hint">${escapeHtml(hint)}</span>` : ''}
            </span>
          </label>`;
      })
      .join('');
  }

  function presetButtons(scope: string): string {
    if (catalogue.presets.length === 0) return '';
    return `
      <div class="staff-presets">
        <span class="muted">Start from:</span>
        ${catalogue.presets
          .map(
            (p) =>
              `<button type="button" class="ghost staff-preset" data-scope="${scope}" data-preset="${escapeHtml(p.name)}">${escapeHtml(p.name)}</button>`,
          )
          .join('')}
      </div>`;
  }

  function render(): void {
    const rows = staff
      .map((m) => {
        const isSelf = m.id === deps.currentUserId;
        const caps = new Set(m.permissions);
        return `
          <div class="card staff-card" data-user="${escapeHtml(m.id)}">
            <div class="staff-head">
              <div>
                <strong>${escapeHtml(m.email)}</strong>
                ${isSelf ? '<span class="pill">you</span>' : ''}
                <div class="muted" style="font-size:12px">
                  ${m.permissions.length} ${m.permissions.length === 1 ? 'capability' : 'capabilities'}
                  ${m.lastLoginAt ? ` · last signed in ${escapeHtml(new Date(m.lastLoginAt).toLocaleString())}` : ' · never signed in'}
                </div>
              </div>
              <div class="staff-actions">
                <button type="button" class="ghost staff-edit" data-user="${escapeHtml(m.id)}">Edit access</button>
                ${
                  isSelf
                    ? ''
                    : `<button type="button" class="ghost staff-remove" data-user="${escapeHtml(m.id)}">Remove</button>`
                }
              </div>
            </div>
            <div class="staff-editor hidden" data-editor="${escapeHtml(m.id)}">
              ${presetButtons(`edit-${m.id}`)}
              <div class="staff-caps">${checklist(`edit-${m.id}`, caps)}</div>
              <div class="staff-editor-actions">
                <button type="button" class="primary staff-save" data-user="${escapeHtml(m.id)}">Save access</button>
                <span class="muted staff-msg" data-msg="${escapeHtml(m.id)}"></span>
              </div>
            </div>
          </div>`;
      })
      .join('');

    bodyEl.innerHTML = `
      ${rows || '<p class="muted">No staff yet.</p>'}
      <div class="card">
        <h2>Add staff member</h2>
        <p class="muted">
          The member signs in with this email and password. Only capabilities you hold yourself are
          listed — ΛPX determines which are available to integrators at all.
        </p>
        <div class="staff-new-grid">
          <label>Email<input id="staff-new-email" type="email" placeholder="finance@yourcompany.com"></label>
          <label>Password<input id="staff-new-password" type="password" placeholder="At least 8 characters"></label>
        </div>
        ${presetButtons('new')}
        <div class="staff-caps">${checklist('new', new Set())}</div>
        <div class="staff-editor-actions">
          <button type="button" class="primary" id="staff-create">Add staff member</button>
          <span class="muted" id="staff-new-msg"></span>
        </div>
      </div>`;

    bind();
  }

  function selectedCaps(scope: HTMLElement): string[] {
    return Array.from(scope.querySelectorAll<HTMLInputElement>('input[data-cap]:checked')).map(
      (i) => i.dataset.cap!,
    );
  }

  /** Surface the server's refusal verbatim — it names the offending capability. */
  function describe(err: unknown): string {
    if (err instanceof AdminApiError) {
      const body = err.details as { reason?: string; permissions?: string[] } | undefined;
      if (body?.reason === 'NOT_TENANT_GRANTABLE') {
        return `ΛPX does not allow integrators to grant: ${(body.permissions ?? []).join(', ')}`;
      }
      if (body?.reason === 'EXCEEDS_GRANTING_ADMIN') {
        return `You do not hold: ${(body.permissions ?? []).join(', ')}`;
      }
      if (body?.reason === 'ACCOUNT_OUTSIDE_TENANT') {
        return 'That email already belongs to an account outside this integrator.';
      }
      return err.message;
    }
    return err instanceof Error ? err.message : String(err);
  }

  function bind(): void {
    bodyEl.querySelectorAll<HTMLButtonElement>('.staff-preset').forEach((btn) => {
      btn.addEventListener('click', () => {
        const preset = catalogue.presets.find((p) => p.name === btn.dataset.preset);
        if (!preset) return;
        const scope = btn.closest('.card')!;
        const wanted = new Set(preset.permissions);
        scope.querySelectorAll<HTMLInputElement>('input[data-cap]').forEach((i) => {
          i.checked = wanted.has(i.dataset.cap!);
        });
      });
    });

    bodyEl.querySelectorAll<HTMLButtonElement>('.staff-edit').forEach((btn) => {
      btn.addEventListener('click', () => {
        const editor = bodyEl.querySelector<HTMLElement>(`[data-editor="${btn.dataset.user}"]`);
        editor?.classList.toggle('hidden');
      });
    });

    bodyEl.querySelectorAll<HTMLButtonElement>('.staff-save').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const userId = btn.dataset.user!;
        const editor = bodyEl.querySelector<HTMLElement>(`[data-editor="${userId}"]`)!;
        const msg = bodyEl.querySelector<HTMLElement>(`[data-msg="${userId}"]`)!;
        btn.disabled = true;
        msg.textContent = 'Saving…';
        try {
          await deps.client.setTenantStaffCapabilities(
            deps.integratorId,
            userId,
            selectedCaps(editor),
          );
          showToast('Access updated.', 'success');
          await load();
          render();
        } catch (err) {
          msg.textContent = describe(err);
          btn.disabled = false;
        }
      });
    });

    bodyEl.querySelectorAll<HTMLButtonElement>('.staff-remove').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const userId = btn.dataset.user!;
        const member = staff.find((m) => m.id === userId);
        const ok = await askConfirm(
          `Remove ${member?.email ?? 'this staff member'}? They lose access to this integrator immediately. The account itself is not deleted.`,
          { danger: true },
        );
        if (!ok) return;
        try {
          await deps.client.removeTenantStaff(deps.integratorId, userId);
          showToast('Removed.', 'success');
          await load();
          render();
        } catch (err) {
          showToast(describe(err), 'error');
        }
      });
    });

    const createBtn = bodyEl.querySelector<HTMLButtonElement>('#staff-create');
    createBtn?.addEventListener('click', async () => {
      const email = bodyEl.querySelector<HTMLInputElement>('#staff-new-email')!.value.trim();
      const password = bodyEl.querySelector<HTMLInputElement>('#staff-new-password')!.value;
      const msg = bodyEl.querySelector<HTMLElement>('#staff-new-msg')!;
      const card = createBtn.closest('.card')!;
      const permissions = selectedCaps(card as HTMLElement);

      if (!email || password.length < 8) {
        msg.textContent = 'Email and a password of at least 8 characters are required.';
        return;
      }
      if (permissions.length === 0) {
        msg.textContent = 'Select at least one capability — an account with none cannot sign in.';
        return;
      }

      createBtn.disabled = true;
      msg.textContent = 'Adding…';
      try {
        await deps.client.createTenantStaff(deps.integratorId, { email, password, permissions });
        const grantedLabels = permissions.map((c) => labelFor(c).label).join(', ');
        showToast(`${email} added with: ${grantedLabels}`, 'success');
        await load();
        render();
      } catch (err) {
        msg.textContent = describe(err);
        createBtn.disabled = false;
      }
    });
  }

  try {
    await load();
    render();
  } catch (err) {
    bodyEl.innerHTML = `<p class="err">${escapeHtml(describe(err))}</p>`;
  }
}
